import {
    useConnectionState,
    useLocalParticipant,
    useParticipants,
    useRoomContext,
} from "@livekit/components-react"
import { ConnectionQuality, ParticipantEvent, RoomEvent } from "livekit-client"
import { useEffect, useRef } from "react"
import LiveEventBuffer from "../../../data/services/LiveEventBuffer"
import moment from "moment"
import {
    addParticipant,
    addPoorConnectionId,
    addRaisedHand,
    newLiveSessionLog,
    removePoorConnectionId,
    removeRaisedHand,
    setActivityCameraOn,
    setIsLocalPoorConnection,
    setIsOutOfApp,
    setIssoundMode,
    setLiveParticipants,
    setPoorConnectionQualityIds,
    setVoiceRecordingIds,
    setOngoingCheckpointIds,
    setShowRaiseHandCheckpoint,
    setCurrentStudentActivity,
    setSeenStudentActivity,
} from "../../../data/redux/actions/liveMode"
import { POOR_CONNECTION_DEBOUNCE_MS } from "../constants"
import {
    publishedCameraTrack,
    readCameraSendStats,
    restartCameraIfItStoppedSending,
    singleFlight,
    videoStatsPayload,
} from "../localCameraHealth"
import { apiRequest } from "../../../data/redux/actions/api"
import { liveServiceV2 } from "../../../data/services/liveServiceV2"
import { connect } from "react-redux"
import { hasRole } from "../../../data/utility/ability"
import { hasFeature } from "../../../data/utility/Utils"
import { PlaySoundM } from "../../components/playSound/PlaySoundM"
import useLiveKitEventListener from "../../hooks/livekit/useLiveKitEventListener"
import { useGhostParticipantRecovery } from "../../hooks/livekit/useGhostParticipantRecovery"
import { useIsSoundMode } from "../../hooks/livekit/useParticipantState"
import { SnackBarM } from "../../components/alert/snackbar/SnackBar"
import { AppState, NativeEventSubscription } from "react-native"
import { ConnectionState } from "livekit-client"
import useThrottledSetAttributes from "../../../engine/live/room/useThrottledSetAttributes"
import { isStudentLiveScreenMounted } from "../../../engine/live/student/liveScreenPresence"

// The activity a student device has in front of the student: its open one
// (`activity_id`), unless the new student space says it is hidden
// (`activity_on_screen` = "0", on the visio or re-reading a finished
// activity). An older client never sets `activity_on_screen`: its open
// activity counts as seen, as before.
function seenActivityOf(attributes) {
    if (attributes?.activity_on_screen === "0") return null
    return Number(attributes?.activity_id) || null
}

function LKEvents(props) {
    const room = useRoomContext()
    const setAttributesThrottled = useThrottledSetAttributes()

    if (!room) return null

    const participants = useParticipants()
    const localParticipant = useLocalParticipant()

    useLiveKitEventListener()

    const connectionState = useConnectionState()
    const isConnected =
        connectionState?.toString() === ConnectionState.Connected
    const isConnectedRef = useRef(isConnected)
    useEffect(() => {
        isConnectedRef.current = isConnected
    }, [isConnected])

    // Audio reliability: auto-recover a "ghost" student whose media path died in
    // background (dialogue mode only) + react to the server's force_reconnect signal.
    const localIsSoundMode = useIsSoundMode(props.user?.id)
    useGhostParticipantRecovery({
        room,
        user: props.user,
        isSoundMode: localIsSoundMode,
        isConnected,
        // Le rejoin après une récupération est explicite : il lui faut de quoi
        // se reconnecter, personne d'autre ne le fera.
        roomData: props.roomData,
    })

    // Observabilité de la reconnexion. C'est le SDK qui la possède désormais,
    // donc c'est lui qu'on écoute — l'app n'a plus de reprise en main à
    // journaliser. Sans ça, une coupure en production ne laisse aucune trace.
    //
    // Seul `reason` traverse : `events-batch` valide les clés une à une et
    // jette le reste, donc pas de durée ici sans changement backend.
    // Deux lignes par coupure suffisent à la reconstituer côté logs.
    //
    // `live_session_logs` est un journal d'événements rares, et ces événements
    // SDK partent à chaque blip de signal : un réseau très instable pourrait en
    // émettre des dizaines par séance. Budget par séance — la dernière ligne dit
    // que le journal est tronqué, la suite est silencieuse. ~10 coupures
    // journalisées suffisent à qualifier la séance.
    const sdkReconnectLinesRef = useRef(0)
    useEffect(() => {
        sdkReconnectLinesRef.current = 0
    }, [props.liveSessionId])

    useEffect(() => {
        const SDK_RECONNECT_LOG_BUDGET = 30
        const record = (reason: string) => {
            // L'écran du live de l'élève (design system) écrit sa propre
            // ligne, une par coupure, avec sa durée : celles du SDK la
            // tripleraient dans le BO.
            if (isStudentLiveScreenMounted()) return
            const used = sdkReconnectLinesRef.current
            if (used > SDK_RECONNECT_LOG_BUDGET) return
            sdkReconnectLinesRef.current = used + 1
            LiveEventBuffer.getInstance().record("auto_reconnect", {
                reason:
                    used === SDK_RECONNECT_LOG_BUDGET
                        ? "sdk_log_capped"
                        : reason,
            })
        }

        const onSignalReconnecting = () => record("sdk_signal_reconnecting")
        const onReconnecting = () => record("sdk_reconnecting")
        const onReconnected = () => {
            record("sdk_reconnected")
            LiveEventBuffer.getInstance().flush()
        }

        // Les deux départs n'émettent PAS le même événement, et c'est le resume
        // — le cas que cette PR protège — qui n'émet pas `Reconnecting` :
        //   resume         : EngineEvent.Resuming  -> RoomEvent.SignalReconnecting
        //   reconnect plein : handleRestarting     -> RoomEvent.Reconnecting
        //   hors ligne      : EngineEvent.Offline  -> RoomEvent.Reconnecting
        // Les deux arrivent sur `Reconnected`. Sans la première ligne, un resume
        // réussi laisse un `sdk_reconnected` orphelin, et un resume qui s'enlise
        // ne laisse rien du tout — l'état reste `SignalReconnecting` sans autre
        // transition à émettre. C'est justement ce qu'on veut surveiller.
        room.on(RoomEvent.SignalReconnecting, onSignalReconnecting)
        room.on(RoomEvent.Reconnecting, onReconnecting)
        room.on(RoomEvent.Reconnected, onReconnected)
        return () => {
            room.off(RoomEvent.SignalReconnecting, onSignalReconnecting)
            room.off(RoomEvent.Reconnecting, onReconnecting)
            room.off(RoomEvent.Reconnected, onReconnected)
        }
    }, [room])

    // Saturation machine : l'encodeur WebRTC signale qu'il est limité par le
    // CPU (`qualityLimitationReason: cpu`), pas par le réseau. C'est la trace
    // qui permet de distinguer, dans les live logs, une « connexion faible »
    // causée par un PC qui ne suit pas (caméra allumée) d'un vrai problème
    // réseau. Même mécanisme de budget que les reconnexions SDK ci-dessus :
    // l'événement peut flapper sur une machine à la limite, quelques lignes
    // suffisent à qualifier la séance. `reason` porte la source du track
    // (camera / screen_share). Ce composant est monté sur toutes les
    // plateformes : des lignes peuvent aussi venir de clients natifs —
    // filtrer par `device_name` pour une analyse web-only.
    const cpuConstrainedLinesRef = useRef(0)
    useEffect(() => {
        cpuConstrainedLinesRef.current = 0
    }, [props.liveSessionId])

    useEffect(() => {
        const CPU_CONSTRAINED_LOG_BUDGET = 10
        const onCpuConstrained = (track) => {
            const used = cpuConstrainedLinesRef.current
            if (used > CPU_CONSTRAINED_LOG_BUDGET) return
            cpuConstrainedLinesRef.current = used + 1
            LiveEventBuffer.getInstance().record("cpu_constrained", {
                reason:
                    used === CPU_CONSTRAINED_LOG_BUDGET
                        ? "log_capped"
                        : track?.source,
            })
        }

        room.localParticipant.on(
            ParticipantEvent.LocalTrackCpuConstrained,
            onCpuConstrained
        )
        return () => {
            room.localParticipant.off(
                ParticipantEvent.LocalTrackCpuConstrained,
                onCpuConstrained
            )
        }
    }, [room])

    // Au retour dans l'app, deux choses peuvent rester bloquées : la caméra, que
    // l'OS a coupée en arrière-plan et qui ne repart pas toujours (observé sur
    // Android), et la mesure de qualité, qui hérite alors d'un « poor » né
    // pendant l'arrière-plan et ne bouge plus jusqu'à ce que la caméra soit
    // recréée. On relance la caméra SEULEMENT si elle n'envoie plus d'images, et
    // on recommence la mesure depuis l'état courant.
    //
    // Le contrôle dure quelques secondes : deux allers-retours rapprochés ne
    // doivent pas relancer la caméra deux fois.
    const checkCameraOnce = useRef(
        singleFlight(async (currentRoom) => {
            if (await restartCameraIfItStoppedSending(currentRoom)) {
                LiveEventBuffer.getInstance().record("camera_restarted", {
                    reason: "foreground_no_frames",
                })
                LiveEventBuffer.getInstance().flush()
            }
        })
    ).current
    const handleReturnToApp = () => {
        LiveEventBuffer.getInstance().closeQualityEpisode("returned_to_app")
        rearmLocalQuality()
        checkCameraOnce(room).catch((e) =>
            console.log("Camera health check failed", e)
        )
    }

    const appState = useRef(AppState.currentState)
    const handleAppStateChange = async (nextAppState) => {
        if (!isConnectedRef.current) return
        if (
            appState.current === "active" &&
            nextAppState.match(/inactive|background/)
        ) {
            console.log("App has gone to the background!")
            setAttributesThrottled({
                out_of_app: "1",
                display_name: props.user?.display_name,
            })
                .then()
                .catch((e) => console.log("Error setting out_of_app", e))
        } else if (
            appState.current.match(/inactive|background/) &&
            nextAppState === "active"
        ) {
            console.log("App has come to the foreground!")
            setAttributesThrottled({
                out_of_app: "",
                display_name: "",
            })
                .then()
                .catch((e) => console.log("Error setting out_of_app", e))
            handleReturnToApp()
        }
        appState.current = nextAppState
    }

    const appStateChangeListener = useRef<NativeEventSubscription>(null)
    const wasConnectedRef = useRef(false)

    useEffect(() => {
        if (isConnected) {
            appStateChangeListener.current = AppState.addEventListener(
                "change",
                handleAppStateChange
            )
            setAttributesThrottled({
                out_of_app: "999",
                activity_camera_on: "999",
                ongoing_checkpoint: "999",
            })
                .then(() => {})
                .catch((e) =>
                    console.error("Error setting initial attributes values", e)
                )

            // Re-sync state from backend on reconnection (not first connect)
            if (wasConnectedRef.current && props.liveSessionId) {
                props.apiRequest(liveServiceV2.getCallV2, { id: props.liveSessionId })
                // The reconnect arrives with empty LiveKit attributes (the token
                // does not seed them), so other participants — notably the tutor
                // — receive AttributesChanged({sound_mode:""}) and see this user
                // in concentré. Re-publish the local truth from Redux.
                // L'écran du live de l'élève (design system) repose lui-même le mode, depuis ce que le
                // serveur dit : Redux vient de lire l'attribut vide de la reconnexion comme un Concentré.
                if (!isStudentLiveScreenMounted()) {
                    setAttributesThrottled({
                        sound_mode: localIsSoundMode ? "1" : "0",
                        display_name: props.user?.display_name ?? "",
                    })
                        .then(() => {})
                        .catch((e) =>
                            console.error("Error re-publishing attributes on reconnect", e)
                        )
                }
            }
            wasConnectedRef.current = true
        } else {
            appStateChangeListener.current?.remove()
        }

        return () => {
            appStateChangeListener.current?.remove()
        }
    }, [isConnected])

    useEffect(() => {
        if (!isConnected || !room) return

        if (props.activityCameraOn) {
            setAttributesThrottled({
                activity_camera_on: "1",
                display_name: props.user?.display_name,
            })
                .then()
                .catch((e) =>
                    console.error("Error setting activity_camera_on", e)
                )
        } else {
            setAttributesThrottled({
                activity_camera_on: "",
                display_name: "",
            })
                .then()
                .catch((e) =>
                    console.error("Error setting activity_camera_on", e)
                )
        }
    }, [props.activityCameraOn, isConnected])

    // Track previous quality state to detect transitions
    const previousQualityRef = useRef<Map<string, ConnectionQuality>>(new Map())

    // Per-participant debounce timers + fired flag. A timer is armed when the
    // quality transitions to Poor/Lost and disarmed if it recovers within
    // POOR_CONNECTION_DEBOUNCE_MS. Only if the timer fires do we propagate the
    // degradation downstream (event buffer, Redux, banner UI). Symmetry: the
    // recovery is only emitted if the degradation had been emitted.
    const poorTimersRef = useRef<
        Map<string, { timer: ReturnType<typeof setTimeout>; fired: boolean }>
    >(new Map())

    // `quality` seul ne dit pas d'où vient la dégradation : LiveKit note aussi
    // « poor » une caméra qui n'envoie plus rien alors que le réseau va bien.
    // Les stats d'envoi, prises au début et à la fin de la temporisation,
    // séparent les deux — et `frames_sent_delta` à 0 nomme le cas caméra.
    const emitDegradation = async (quality, participant, baseline) => {
        if (participant.isLocal) {
            const [before, after] = await Promise.all([
                baseline,
                readCameraSendStats(publishedCameraTrack(room)),
            ])
            const video = videoStatsPayload(before, after)

            LiveEventBuffer.getInstance().record("quality_degraded", {
                quality: quality === ConnectionQuality.Lost ? "lost" : "poor",
                ...(video ? { video } : {}),
            })
            props.setIsLocalPoorConnection(true)
            return
        }
        const participantId = parseInt(participant.identity.split(".")[0])
        props.addPoorConnectionId(participantId)
    }

    const emitRecovery = (quality, participant) => {
        if (participant.isLocal) {
            LiveEventBuffer.getInstance().record("quality_recovered", {
                quality:
                    quality === ConnectionQuality.Excellent
                        ? "excellent"
                        : "good",
            })
            props.setIsLocalPoorConnection(false)
            return
        }
        const participantId = parseInt(participant.identity.split(".")[0])
        props.removePoorConnectionId(participantId)
    }

    const clearPoorTimer = (participantKey: string) => {
        const entry = poorTimersRef.current.get(participantKey)
        if (entry) {
            clearTimeout(entry.timer)
            poorTimersRef.current.delete(participantKey)
        }
    }

    const armPoorTimer = (participantKey: string, quality, participant) => {
        clearPoorTimer(participantKey)
        // Photo de la caméra à l'instant de la chute, avant la temporisation.
        const baseline = participant.isLocal
            ? readCameraSendStats(publishedCameraTrack(room))
            : Promise.resolve(null)

        const timer = setTimeout(() => {
            const entry = poorTimersRef.current.get(participantKey)
            if (!entry) return
            entry.fired = true
            emitDegradation(quality, participant, baseline)
        }, POOR_CONNECTION_DEBOUNCE_MS)
        poorTimersRef.current.set(participantKey, { timer, fired: false })
    }

    const resetQualityTracking = () => {
        poorTimersRef.current.forEach(({ timer }) => clearTimeout(timer))
        poorTimersRef.current.clear()
        previousQualityRef.current.clear()
        props.setIsLocalPoorConnection(false)
    }

    /**
     * Repart de l'état courant de la connexion locale : ce que LiveKit en disait
     * avant le retour dans l'app décrivait une app en arrière-plan. Toujours
     * dégradé 15 s plus tard, le client le dira de nouveau — et cette
     * dégradation-là, mesurée dans l'app, compte.
     */
    const rearmLocalQuality = () => {
        const local = room.localParticipant
        if (!local) return

        clearPoorTimer(local.identity)
        previousQualityRef.current.set(local.identity, local.connectionQuality)

        if (
            [ConnectionQuality.Lost, ConnectionQuality.Poor].includes(
                local.connectionQuality
            )
        ) {
            armPoorTimer(local.identity, local.connectionQuality, local)
        }
    }

    // Handler that runs when any participant's connection quality changes
    const handleConnectionQualityChanged = (quality, participant) => {
        const participantKey = participant.identity
        const previousQuality = previousQualityRef.current.get(participantKey)
        const isPoorOrLost = [
            ConnectionQuality.Lost,
            ConnectionQuality.Poor,
        ].includes(quality)
        const wasPoorOrLost =
            previousQuality !== undefined &&
            [ConnectionQuality.Lost, ConnectionQuality.Poor].includes(
                previousQuality
            )

        // Update previous quality tracking
        previousQualityRef.current.set(participantKey, quality)

        if (isPoorOrLost) {
            // Flicker between Poor and Lost: keep the existing timer running.
            if (wasPoorOrLost) return

            // Transition Good/Excellent -> Poor/Lost: arm the debounce timer.
            // Nothing else happens until the timer fires.
            armPoorTimer(participantKey, quality, participant)
            return
        }

        // Transition Poor/Lost -> Good/Excellent
        const entry = poorTimersRef.current.get(participantKey)
        if (!entry) return

        if (entry.fired) {
            // The degradation was visible — emit the symmetric recovery.
            emitRecovery(quality, participant)
        }
        // If the timer was still armed, the micro-blip stays invisible.
        clearPoorTimer(participantKey)
    }

    // Reset the poor-connection list on effect mount. Existing degraded
    // participants will be picked up through ConnectionQualityChanged events
    // (which go through the 15s debounce) — initializing the list directly
    // here would bypass the debounce on session join.
    const updateAllConnectionQualities = () => {
        if (!room || !isConnected) return
        props.setPoorConnectionQualityIds([])
    }

    const updateAllCheckpointStatuses = () => {
        // Synchronize initial checkpoint states
        if (hasRole(props.user, "tutor")) {
            const activeCheckpoints = []

            // Check remote participants
            room.remoteParticipants.forEach((participant) => {
                const participantId = parseInt(
                    participant.identity.split(".")[0]
                )
                if (participant.attributes?.ongoing_checkpoint === "1") {
                    activeCheckpoints.push(participantId)
                }
            })

            if (activeCheckpoints.length > 0) {
                props.setOngoingCheckpointIds(activeCheckpoints)
            }
        }
    }

    const updateAllStudentActivities = () => {
        // Synchronize initial student activity states
        if (hasRole(props.user, "tutor")) {
            // Use participants from useParticipants hook (more reliable than room.remoteParticipants)
            participants.forEach((participant) => {
                // Skip local participant
                if (participant.isLocal) return

                const participantId = parseInt(
                    participant.identity.split(".")[0]
                )
                const activityId = participant.attributes?.activity_id
                if (activityId) {
                    props.setCurrentStudentActivity(
                        participantId,
                        Number(activityId)
                    )
                }
            })
            // What each device in the room shows, rebuilt as devices come and
            // go (see `seenActivityOf`).
            props.setSeenStudentActivity(
                Object.fromEntries(
                    participants
                        .filter((participant) => !participant.isLocal)
                        .map((participant) => [
                            participant.identity,
                            seenActivityOf(participant.attributes),
                        ])
                ),
                true
            )
        }
    }

    const handleParticipantConnected = (participant) => {
        const participantId = parseInt(
            participant.identity.split(".")[0] ?? participant.identity
        )
        props.newLiveSessionLog({
            user_id: participantId,
            live_session_id: props.liveSessionId,
            action: "joined_call",
            created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
        })
        props.addParticipant(participantId)

        if (hasRole(props.user, "tutor")) {
            PlaySoundM.play(require("../../../../assets/sounds/glass_006.mp3"))
        }
    }

    const handleParticipantDisconnected = (participant) => {
        const participantId =
            participant.identity.split(".")[0] ?? participant.identity
        props.newLiveSessionLog({
            user_id: parseInt(participantId),
            live_session_id: props.liveSessionId,
            action: "left_call",
            created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
        })
        // Drop any pending poor-connection debounce for this participant so it
        // never fires after they've left the room. A remote participant who
        // had been flagged as poor also disappears from the banner because
        // ConnectionQualityAlert filters poorConnectionQualityIds against
        // current liveParticipants.
        clearPoorTimer(participant.identity)
        if (hasRole(props.user, "tutor")) {
            PlaySoundM.play(require("../../../../assets/sounds/error_004.mp3"))
            // A device that left no longer says what the student sees.
            props.setSeenStudentActivity({ [participant.identity]: undefined })
        }
    }

    const handleAttributesChanged = (record, participant) => {
        const participantId = parseInt(participant.identity)
        const updatedAttributes = participant.attributes

        // In events-panel rooms these signals surface in the panel, so the
        // legacy global toasts are suppressed to avoid a double notification.
        const eventsPanelEnabled = hasFeature(
            props.liveSessions?.find((s) => s?.id === props.liveSessionId)
                ?.live_room,
            "events-panel"
        )

        if (record?.hand_raised !== undefined) {
            if (updatedAttributes?.hand_raised === "1") {
                props.addRaisedHand(participantId)
                if (hasRole(props.user, "tutor")) {
                    PlaySoundM.play(
                        require("../../../../assets/sounds/toggle_001.mp3")
                    )
                    if (!eventsPanelEnabled) {
                        SnackBarM.show({
                            text1: `✋ ${updatedAttributes?.display_name} a levé la main`,
                            duration: 4000,
                        })
                    }
                }
            } else {
                props.removeRaisedHand(participantId)
            }
            if (hasRole(props.user, "tutor")) {
                if (updatedAttributes?.raise_hand_source !== "checkpoint") {
                    props.setShowRaiseHandCheckpoint(participantId, true)
                } else {
                    props.setShowRaiseHandCheckpoint(participantId, false)
                }
            }
        }

        if (record?.ongoing_checkpoint !== undefined) {
            if (
                updatedAttributes?.ongoing_checkpoint === "1" &&
                hasRole(props.user, "tutor")
            ) {
                if (!props.ongoingCheckpointIds.includes(participantId)) {
                    props.setOngoingCheckpointIds([participantId], "add")
                    if (!eventsPanelEnabled) {
                        SnackBarM.show({
                            text1: `🤖 ${
                                updatedAttributes?.display_name || "Un élève"
                            } commence une conversation avec Sam`,
                            duration: 5000,
                        })
                    }
                }
            } else {
                props.setOngoingCheckpointIds([participantId], "remove")
            }
        }

        if (record?.sound_mode !== undefined) {
            const isSoundMode = updatedAttributes.sound_mode === "1"
            props.setIssoundMode(isSoundMode, participantId)
            props.setOngoingCheckpointIds([participantId], "remove")

            if (
                localParticipant.localParticipant.identity ===
                    participant.identity &&
                hasRole(props.user, "student") &&
                // L'écran du live de l'élève (design system) dit lui-même le mode : son son, Sam.
                !isStudentLiveScreenMounted()
            ) {
                const message = !isSoundMode
                    ? `${
                          updatedAttributes?.display_name ?? "Ton masteur"
                      } t'a mis en mode concentré`
                    : `${
                          updatedAttributes?.display_name ?? "Ton masteur"
                      } t'a mis en mode dialogue`
                const sound = !isSoundMode
                    ? require("../../../../assets/sounds/minimize_001_low.mp3")
                    : require("../../../../assets/sounds/maximize_001_low.mp3")
                PlaySoundM.play(sound)
                SnackBarM.show({ text1: message, duration: 3000 })
            }
        }
        if (record?.out_of_app !== undefined) {
            const isOutOfApp = updatedAttributes.out_of_app === "1"
            props.setIsOutOfApp(isOutOfApp, participantId)
        }
        if (record?.activity_camera_on !== undefined) {
            const activityCameraOn =
                updatedAttributes.activity_camera_on === "1"
            props.setActivityCameraOn(activityCameraOn, participantId)
        }
        if (record?.voice_recording_activity !== undefined) {
            const isRecordingActivity =
                updatedAttributes?.voice_recording_activity === "1"
            props.setVoiceRecordingIds(isRecordingActivity, participantId)
        }
        if (record?.activity_id !== undefined) {
            props.setCurrentStudentActivity(
                participantId,
                Number(updatedAttributes?.activity_id)
            )
        }
        if (
            hasRole(props.user, "tutor") &&
            (record?.activity_id !== undefined ||
                record?.activity_on_screen !== undefined)
        ) {
            props.setSeenStudentActivity({
                [participant.identity]: seenActivityOf(updatedAttributes),
            })
        }
    }

    useEffect(() => {
        if (!room) return

        const participantConnectedHandler = (participant) =>
            handleParticipantConnected(participant)
        const participantDisconnectedHandler = (participant) =>
            handleParticipantDisconnected(participant)
        const participantAttributesChangedHandler = (record, participant) =>
            handleAttributesChanged(record, participant)
        // La mesure s'arrête avec la connexion : une dégradation ouverte n'a
        // plus de fin à attendre, et son état ne doit pas se transporter dans
        // la séance suivante (le SDK n'émet rien tant que la qualité ne change
        // pas, donc un `recovered` viendrait sinon manquer).
        const disconnectedHandler = () => {
            LiveEventBuffer.getInstance().closeQualityEpisode("disconnected")
            resetQualityTracking()
        }

        // Register event listeners
        room.on(RoomEvent.ParticipantConnected, participantConnectedHandler)
        room.on(
            RoomEvent.ParticipantDisconnected,
            participantDisconnectedHandler
        )
        room.on(
            RoomEvent.ParticipantAttributesChanged,
            participantAttributesChangedHandler
        )
        room.on(
            RoomEvent.ConnectionQualityChanged,
            handleConnectionQualityChanged
        )
        room.on(RoomEvent.Disconnected, disconnectedHandler)

        // Initial connection quality check
        updateAllConnectionQualities()
        updateAllCheckpointStatuses()

        return () => {
            room.off(
                RoomEvent.ParticipantConnected,
                participantConnectedHandler
            )
            room.off(
                RoomEvent.ParticipantDisconnected,
                participantDisconnectedHandler
            )
            room.off(
                RoomEvent.ParticipantAttributesChanged,
                participantAttributesChangedHandler
            )
            room.off(
                RoomEvent.ConnectionQualityChanged,
                handleConnectionQualityChanged
            )
            room.off(RoomEvent.Disconnected, disconnectedHandler)
            // Clear any pending poor-connection debounce timers so they
            // can't fire after the room is gone, and reset the local poor
            // flag in case a timer had fired without a subsequent recovery.
            resetQualityTracking()
        }
    }, [room, props.user?.id]) // Re-run when room or user ID changes

    // TODO : state redux redundant with LiveKit's useParticipants. Remove it.
    useEffect(() => {
        if (participants?.length > 0) {
            const ids = participants.map((x) =>
                parseInt(x.identity.split(".")[0])
            )
            props.setLiveParticipants(ids)
        }
    }, [participants?.length])

    // Initialize student activities when connected and participants are available
    useEffect(() => {
        if (isConnected && participants?.length > 0) {
            updateAllStudentActivities()
        }
    }, [isConnected, participants?.length])

    return null
}

const mapStateToProps = (state) => {
    return {
        liveSessionId: state.liveMode?.liveSessionId,
        liveSessions: state.data.currentUser?.object?.live_sessions,
        // `participants` (Redux), `poorConnectionQualityIds`, `activeTime`,
        // `timesSinceLastCall` were subscribed here but never read by the
        // component (participants is shadowed by useParticipants() locally).
        // Removed to stop re-rendering on metric ticks and poor-connection
        // updates.
        user: state.data.currentUser?.object,
        activityCameraOn: state.activity?.cameraOn,
        ongoingCheckpointIds: state.liveMode?.ongoingCheckpointIds || [],
        roomData: state.liveKitRoom?.roomData,
    }
}

const mapDispatchToProps = {
    apiRequest,
    addParticipant,
    setLiveParticipants,
    setVoiceRecordingIds,
    setIssoundMode,
    addRaisedHand,
    removeRaisedHand,
    setIsOutOfApp,
    setActivityCameraOn,
    newLiveSessionLog,
    setPoorConnectionQualityIds,
    addPoorConnectionId,
    removePoorConnectionId,
    setIsLocalPoorConnection,
    setOngoingCheckpointIds,
    setShowRaiseHandCheckpoint,
    setCurrentStudentActivity,
    setSeenStudentActivity,
}

export default connect(mapStateToProps, mapDispatchToProps)(LKEvents)
